import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from 'react-redux';
import OkrFinancialTargetDialog from './OkrFinancialTargetDialog';
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { getQuickBooksFinancialIndicators, getOkrFinancialTargets, saveOkrFinancialTargets } from "./okrApi";
import { cycleDateRange } from "./okrCycleHelpers";
import "./okr.css";
import OkrWeeklyResultsReport from "./OkrWeeklyResultsReport";
import KpiCard from "./OkrFinancialKpiCard";
import { financialSalesTargets, financialMarginTargets, financialMarginRows, configuredFinancialTargets, resolveFinancialTargets, operatingFinancialRows } from "./okrFinancialResults";

const BUSINESS_UNIT_OPTIONS = [
  { label: "Consolidado", value: "consolidated" },
  { label: "Empaque", value: "empaque" },
  { label: "Foods", value: "foods" },
  { label: "Coffee & Vending", value: "coffee_vending" },
];

const ACCOUNTING_METHOD_OPTIONS = [
  { label: "Devengado", value: "Accrual" },
  { label: "Efectivo", value: "Cash" },
];

const getDefaultRange = (cycle) => {
  const today = new Date().toISOString().slice(0, 10);
  const { startDate: cycleStart, endDate: cycleEnd } = cycleDateRange(cycle);
  if (cycleStart && cycleEnd) {
    return { startDate: cycleStart, endDate: cycleEnd };
  }
  return { startDate: `${today.slice(0, 8)}01`, endDate: today };
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("es-PA", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) return "—";
  const literal = String(value).slice(0, 10);
  const [year, month, day] = literal.split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
};

const formatShortDate = (value) => {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return "—";
  return date
    .toLocaleDateString("es-PA", {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    })
    .replace(".", "");
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-PA", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const collectExpandableKeys = (nodes = [], includeNested = true) => {
  const keys = new Set();
  nodes.forEach((node) => {
    if (node?.children?.length) {
      keys.add(node.key);
      if (includeNested) {
        collectExpandableKeys(node.children, true).forEach((key) =>
          keys.add(key)
        );
      }
    }
  });
  return keys;
};

const flattenVisibleRows = (nodes = [], expandedKeys = new Set()) => {
  const rows = [];
  nodes.forEach((node) => {
    rows.push(node);
    if (node?.children?.length && expandedKeys.has(node.key)) {
      rows.push(...flattenVisibleRows(node.children, expandedKeys));
    }
  });
  return rows;
};

const flattenAllRows = (nodes = []) =>
  nodes.flatMap((node) => [node, ...flattenAllRows(node.children || [])]);

// Keep the operational Balance view and its CSV focused on short-term accounts.
const actionableBalanceReport = (report) => ({
  ...report,
  rows: flattenAllRows(report?.rows || [])
    .filter((row) => ["111", "112", "113", "211"].includes(
      String(row.accountCode || String(row.label || "").match(/^(\d+)\b/)?.[1] || "")
    ))
    .map((row) => ({ ...row, depth: 0, children: [] })),
});

const getWeekStatusLabel = (week) => {
  if (week?.status === "current") return "Semana en curso";
  if (week?.status === "future") return "Semana futura";
  return "Semana cerrada";
};

const getWeekStatusSeverity = (week) => {
  if (week?.status === "current") return "warning";
  if (week?.status === "future") return "info";
  return "success";
};

const escapeCsvCell = (value) => {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
};

const downloadWeeklyCsv = ({ report, weeks, reportType, title }) => {
  const headers = [
    "Cuenta / descripción",
    ...weeks.map(
      (week) =>
        `S${week.weekNumber} (${week.startDate} a ${week.endDate})`
    ),
    reportType === "balance" ? "Último cierre" : "Acumulado",
  ];
  const rows = flattenAllRows(report?.rows || []).map((row) => [
    `${"  ".repeat(Math.max(0, Number(row.depth || 0)))}${row.label}`,
    ...(row.weeklyValues || []).map((value) => value ?? ""),
    row.amount ?? "",
  ]);
  const csv = [headers, ...rows]
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const WeeklyFinancialReportTable = ({
  title,
  report,
  weeks,
  reportType,
  selectedWeekIndex,
  onSelectWeek,
}) => {
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const rows = useMemo(() => report?.rows || [], [report?.rows]);

  useEffect(() => {
    setExpandedKeys(collectExpandableKeys(rows, true));
  }, [rows]);

  const visibleRows = useMemo(
    () => flattenVisibleRows(rows, expandedKeys),
    [expandedKeys, rows]
  );

  const toggleRow = (key) => {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <section className="surface-card border-round shadow-1 okr-financial-report">
      <div className="okr-financial-report__header">
        <div>
          <div className="flex flex-wrap align-items-center gap-2">
            <h3 className="m-0">{title}</h3>
            <Tag value="QuickBooks semanal" severity="info" />
          </div>
          <p className="text-color-secondary mt-2 mb-0 text-sm">
            {report?.displayName || title} · Base {report?.reportBasis || "—"} ·
            13 cortes semanales
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            label="Exportar CSV"
            icon="pi pi-download"
            className="p-button-outlined p-button-sm"
            onClick={() =>
              downloadWeeklyCsv({ report, weeks, reportType, title })
            }
          />
          {rows.some((row) => row.children?.length) ? (
            <>
          <Button
            type="button"
            label="Expandir todo"
            icon="pi pi-plus"
            className="p-button-text p-button-sm"
            onClick={() => setExpandedKeys(collectExpandableKeys(rows, true))}
          />
          <Button
            type="button"
            label="Contraer"
            icon="pi pi-minus"
            className="p-button-text p-button-sm"
            onClick={() => setExpandedKeys(new Set())}
          />
            </>
          ) : null}
        </div>
      </div>

      <div className="okr-financial-table-wrap">
        <table className="okr-financial-table okr-financial-table--weekly">
          <thead>
            <tr>
              <th className="okr-financial-table__account-column">
                Cuenta / descripción
              </th>
              {weeks.map((week, weekIndex) => (
                <th
                  key={week.weekNumber}
                  className={
                    weekIndex === selectedWeekIndex ? "is-selected-week" : ""
                  }
                >
                  <button
                    type="button"
                    className="okr-financial-week-heading"
                    onClick={() => onSelectWeek(weekIndex)}
                  >
                    <strong>S{week.weekNumber}</strong>
                    <span>{formatShortDate(week.endDate)}</span>
                  </button>
                </th>
              ))}
              <th className="text-right">
                {reportType === "balance" ? "Último cierre" : "Acumulado"}
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const hasChildren = Boolean(row?.children?.length);
              const isExpanded = expandedKeys.has(row.key);
              return (
                <tr
                  key={row.key}
                  className={row.type === "section" ? "is-section" : ""}
                >
                  <td className="okr-financial-table__account-column">
                    <div
                      className="okr-financial-account"
                      style={{
                        paddingLeft: `${
                          Math.min(Number(row.depth || 0), 6) * 1.1
                        }rem`,
                      }}
                    >
                      {hasChildren ? (
                        <button
                          type="button"
                          className="p-link okr-financial-expand"
                          onClick={() => toggleRow(row.key)}
                          aria-expanded={isExpanded}
                          aria-label={`${
                            isExpanded ? "Contraer" : "Expandir"
                          } ${row.label}`}
                        >
                          <i
                            className={`pi ${
                              isExpanded
                                ? "pi-chevron-down"
                                : "pi-chevron-right"
                            }`}
                          />
                        </button>
                      ) : (
                        <span className="okr-financial-expand-spacer" />
                      )}
                      <span>{row.label}</span>
                    </div>
                  </td>
                  {weeks.map((week, weekIndex) => (
                    <td
                      key={`${row.key}-${week.weekNumber}`}
                      className={`text-right white-space-nowrap okr-financial-week-cell ${
                        weekIndex === selectedWeekIndex ? "is-selected-week" : ""
                      } ${week.status === "future" ? "is-future-week" : ""}`}
                      onClick={() => onSelectWeek(weekIndex)}
                    >
                      {formatMoney(row.weeklyValues?.[weekIndex])}
                    </td>
                  ))}
                  <td className="text-right white-space-nowrap is-total-column">
                    {formatMoney(row.amount)}
                  </td>
                </tr>
              );
            })}
            {!visibleRows.length ? (
              <tr>
                <td
                  colSpan={weeks.length + 2}
                  className="text-center text-color-secondary p-4"
                >
                  El reporte no devolvió filas para este período.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const OkrFinancialIndicators = ({ selectedCycle, keyResults = [] }) => {
  const initialRange = useMemo(
    () => getDefaultRange(selectedCycle),
    [selectedCycle]
  );
  const [businessUnit, setBusinessUnit] = useState("consolidated");
  const [accountingMethod, setAccountingMethod] = useState("Accrual");
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [appliedFilters, setAppliedFilters] = useState({
    businessUnit: "consolidated",
    accountingMethod: "Accrual",
    startDate: initialRange.startDate,
    endDate: initialRange.endDate,
    refresh: false,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [activeReportIndex, setActiveReportIndex] = useState(0);
  const dispatch = useDispatch();
  const [targetSnapshot, setTargetSnapshot] = useState(null);
  const [targetDialog, setTargetDialog] = useState(false);
  const [targetBusy, setTargetBusy] = useState(false);
  const [targetError, setTargetError] = useState('');
  const targetCycleId = selectedCycle?._id;
  const targetUnit = data?.businessUnit || 'consolidated';
  useEffect(() => {
    let cancelled = false;
    setTargetSnapshot(null); setTargetError(''); setTargetDialog(false);
    if (targetCycleId) getOkrFinancialTargets(targetCycleId, targetUnit).then(response => {
      if (cancelled) return;
      if (response?.data) setTargetSnapshot({...response.data, cycleId:targetCycleId, businessUnit:targetUnit});
      else setTargetError('No se pudieron cargar las metas configuradas. Vuelve a abrir esta vista antes de guardar.');
    });
    return () => { cancelled = true; };
  }, [targetCycleId, targetUnit]);
  const openTargets = async () => {
    setTargetBusy(true); setTargetError('');
    try {
      const response = await getOkrFinancialTargets(targetCycleId, targetUnit);
      if (response?.data) { setTargetSnapshot({...response.data, cycleId:targetCycleId, businessUnit:targetUnit}); setTargetDialog(true); }
      else setTargetError('No se pudo cargar la configuración. Inténtalo nuevamente.');
    } finally { setTargetBusy(false); }
  };
  const saveTargets = async payload => {
    setTargetBusy(true); setTargetError('');
    try {
      const response = await dispatch(saveOkrFinancialTargets(targetCycleId, targetUnit, payload));
      if (response?.status >= 200 && response.status < 300 && response.data?.data) {
        setTargetSnapshot({...response.data.data, cycleId:targetCycleId, businessUnit:targetUnit}); setTargetDialog(false);
      } else setTargetError(response?.data?.message || 'No se guardaron las metas. Cierra y vuelve a abrir esta vista para cargar la versión más reciente.');
    } catch (error) { setTargetError('No se pudieron guardar las metas. Inténtalo nuevamente.'); }
    finally { setTargetBusy(false); }
  };


  useEffect(() => {
    setStartDate(initialRange.startDate);
    setEndDate(initialRange.endDate);
    setAppliedFilters((current) => ({
      ...current,
      startDate: initialRange.startDate,
      endDate: initialRange.endDate,
      refresh: false,
    }));
  }, [initialRange]);

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getQuickBooksFinancialIndicators(appliedFilters);
      if (!response?.data) {
        setError("No fue posible obtener los reportes semanales de QuickBooks.");
        return;
      }
      setData(response.data);
    } catch (requestError) {
      setError(
        requestError?.message ||
          "No fue posible obtener los reportes semanales de QuickBooks."
      );
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    const weeks = data?.weeks || [];
    if (!weeks.length) return;
    const currentIndex = weeks.findIndex((week) => week.status === "current");
    if (currentIndex >= 0) {
      setSelectedWeekIndex(currentIndex);
      return;
    }
    const lastClosedIndex = weeks.reduce(
      (latest, week, index) => (week.status === "closed" ? index : latest),
      0
    );
    setSelectedWeekIndex(lastClosedIndex);
  }, [data]);

  const applyFilters = (forceRefresh = false) => {
    if (!startDate || !endDate || startDate > endDate) {
      setError("Selecciona un rango de fechas válido.");
      return;
    }
    setAppliedFilters({
      businessUnit,
      accountingMethod,
      startDate,
      endDate,
      refresh: forceRefresh,
      refreshToken: forceRefresh ? Date.now() : undefined,
    });
  };

  const fullCycleRange = getDefaultRange(selectedCycle);
  const targetsCompatible = Boolean(selectedCycle) &&
    data?.period?.startDate === fullCycleRange.startDate &&
    data?.period?.endDate === fullCycleRange.endDate &&
    data?.period?.accountingMethod === "Accrual";
  const okrTargets = targetsCompatible
    ? { ...financialSalesTargets(keyResults, selectedCycle, data.businessUnit), ...financialMarginTargets(keyResults, selectedCycle, data.businessUnit) }
    : {};
  const configurationReady = targetsCompatible && targetSnapshot?.cycleId === targetCycleId && targetSnapshot?.businessUnit === targetUnit;
  const salesTargets = resolveFinancialTargets(configurationReady ? configuredFinancialTargets(targetSnapshot.targets, selectedCycle) : {}, okrTargets);
  const resultsReport = data ? {
    ...data.profitAndLoss,
    rows: operatingFinancialRows(data.profitAndLoss?.rows, financialMarginRows(data.weeklySummaries, data.weeks, data.businessUnit)),
  } : null;
  const targetNotice = targetsCompatible ? null :
    "Para comparar con las metas OKR, selecciona el rango completo del ciclo y la base Devengado. Se muestran los importes consultados; las metas quedan sin asignar para este filtro.";
  const weeks = data?.weeks || [];
  const selectedWeek = weeks[selectedWeekIndex] || null;
  const cardGoalContext = {
    numberOfWeeks: selectedCycle?.numberOfWeeks,
    weekStatus: selectedWeek?.status,
    targetState: !targetsCompatible || targetError ? "unavailable" : configurationReady ? "ready" : "loading",
  };
  const selectedSummary = data?.weeklySummaries?.[selectedWeekIndex] || {};
  const previousSummary =
    selectedWeekIndex > 0
      ? data?.weeklySummaries?.[selectedWeekIndex - 1] || {}
      : {};
  const profitSummary = selectedSummary.profitAndLoss || {};
  const previousProfitSummary = previousSummary.profitAndLoss || {};
  const selectedWeekHint = selectedWeek
    ? `S${selectedWeek.weekNumber} · ${formatDate(selectedWeek.startDate)}–${formatDate(
        selectedWeek.effectiveEndDate || selectedWeek.endDate
      )}`
    : "Semana no disponible";

  const moveSelectedWeek = (direction) => {
    setSelectedWeekIndex((current) =>
      Math.max(0, Math.min(weeks.length - 1, current + direction))
    );
  };

  return (
    <div className="okr-indicators">
      <div className="surface-card border-round shadow-1 p-3 mb-3">
        <div className="flex flex-column xl:flex-row xl:align-items-end xl:justify-content-between gap-3">
          <div>
            <h3 className="m-0">Indicadores financieros semanales</h3>
            <p className="text-color-secondary mt-2 mb-0">
              Las cuentas completas de Pérdidas y Ganancias y Balance General,
              organizadas en las 13 semanas del ciclo.
            </p>
          </div>
          <div className="okr-indicator-filters">
            <label>
              <span>Empresa</span>
              <Dropdown
                value={businessUnit}
                options={BUSINESS_UNIT_OPTIONS}
                onChange={(event) => setBusinessUnit(event.value)}
              />
            </label>
            <label>
              <span>Desde</span>
              <input
                type="date"
                className="p-inputtext p-component"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </label>
            <label>
              <span>Hasta / corte</span>
              <input
                type="date"
                className="p-inputtext p-component"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </label>
            <label>
              <span>Base</span>
              <Dropdown
                value={accountingMethod}
                options={ACCOUNTING_METHOD_OPTIONS}
                onChange={(event) => setAccountingMethod(event.value)}
              />
            </label>
            <Button
              type="button"
              label="Actualizar"
              icon="pi pi-refresh"
              onClick={() => applyFilters(true)}
              loading={loading}
            />
          </div>
        </div>
        {data?.queriedAt ? (
          <div className="text-color-secondary text-sm mt-3">
            Fuente: QuickBooks en vivo · {data.businessUnitLabel} · Consultado{" "}
            {formatDateTime(data.queriedAt)}
          </div>
        ) : null}
      </div>

      {error ? (
        <Message severity="error" text={error} className="w-full mb-3" />
      ) : null}
      {data?.consolidationNotice ? (
        <Message
          severity="warn"
          text={data.consolidationNotice}
          className="w-full mb-3"
        />
      ) : null}
      {loading && !data ? (
        <div className="surface-card border-round shadow-1 p-6 flex flex-column align-items-center gap-3">
          <ProgressSpinner style={{ width: "42px", height: "42px" }} />
          <span className="text-color-secondary">
            Construyendo las 13 semanas desde QuickBooks…
          </span>
        </div>
      ) : null}

      {data ? (
        <>
          <section className="surface-card border-round shadow-1 p-3 mb-3 okr-financial-week-picker">
            <div>
              <span className="text-color-secondary text-sm">
                Semana mostrada en las tarjetas
              </span>
              <div className="flex flex-wrap align-items-center gap-2 mt-1">
                <strong>{selectedWeekHint}</strong>
                {selectedWeek ? (
                  <Tag
                    value={getWeekStatusLabel(selectedWeek)}
                    severity={getWeekStatusSeverity(selectedWeek)}
                  />
                ) : null}
              </div>
            </div>
            <div className="flex align-items-center gap-2">
              <Button
                type="button"
                icon="pi pi-chevron-left"
                className="p-button-outlined p-button-sm"
                aria-label="Semana anterior"
                disabled={selectedWeekIndex <= 0}
                onClick={() => moveSelectedWeek(-1)}
              />
              <Dropdown
                value={selectedWeekIndex}
                options={weeks.map((week, index) => ({
                  value: index,
                  label: `S${week.weekNumber} · ${formatShortDate(week.startDate)}–${formatShortDate(
                    week.endDate
                  )}`,
                }))}
                onChange={(event) => setSelectedWeekIndex(event.value)}
              />
              <Button
                type="button"
                icon="pi pi-chevron-right"
                className="p-button-outlined p-button-sm"
                aria-label="Semana siguiente"
                disabled={selectedWeekIndex >= weeks.length - 1}
                onClick={() => moveSelectedWeek(1)}
              />
            </div>
          </section>

          <div className="okr-indicator-kpis okr-indicator-kpis--summary mb-3">
            <KpiCard
              label="Ingresos totales"
              {...cardGoalContext}
              target={salesTargets.totalIncome}
              value={profitSummary.totalIncome}
              previousValue={previousProfitSummary.totalIncome}
              hint={selectedWeekHint}
            />
            <KpiCard
              label="Beneficio bruto"
              {...cardGoalContext}
              target={salesTargets.grossProfit}
              value={profitSummary.grossProfit}
              previousValue={previousProfitSummary.grossProfit}
              hint={selectedWeekHint}
            />
            <KpiCard
              label="EBITDA"
              {...cardGoalContext}
              target={salesTargets.ebitda}
              value={profitSummary.ebitda}
              previousValue={previousProfitSummary.ebitda}
              hint="Beneficio bruto − cuentas 61, 62 y 63"
            />
          </div>

          <div className="mb-3">
            <div className="flex flex-wrap align-items-center gap-2 mb-2">
              <h3 className="m-0">Ventas de la semana</h3>
              <Tag value={selectedWeekHint} severity="info" />
            </div>
            <div className="okr-indicator-kpis">
              <KpiCard
                label="Ventas Empaques"
                {...cardGoalContext}
                target={salesTargets['48']}
                value={profitSummary.packagingSales}
                previousValue={previousProfitSummary.packagingSales}
                hint="Total cuenta 48"
              />
              <KpiCard
                label="Alimentos al Mayor"
                {...cardGoalContext}
                target={salesTargets['45']}
                value={profitSummary.wholesaleFoodSales}
                previousValue={previousProfitSummary.wholesaleFoodSales}
                hint="Total cuenta 45"
              />
              <KpiCard
                label="Snacks, Bebidas y Café contado"
                {...cardGoalContext}
                target={salesTargets['41']}
                value={profitSummary.vendingCashSales}
                previousValue={previousProfitSummary.vendingCashSales}
                hint="Total cuenta 41"
              />
              <KpiCard
                label="Contratos Café y Servicios"
                {...cardGoalContext}
                target={salesTargets['42']}
                value={profitSummary.coffeeServiceSales}
                previousValue={previousProfitSummary.coffeeServiceSales}
                hint="Total cuenta 42"
              />
            </div>
          </div>

          <OkrFinancialTargetDialog visible={targetDialog} onHide={() => setTargetDialog(false)} snapshot={targetSnapshot} okrTargets={okrTargets} businessUnit={targetUnit} cycleName={selectedCycle?.name} onSave={saveTargets} busy={targetBusy} error={targetError} />
          <TabView
            activeIndex={activeReportIndex}
            onTabChange={(event) => setActiveReportIndex(event.index)}
            className="okr-financial-weekly-tabs"
          >
            <TabPanel header="Resultados semanales">
              <OkrWeeklyResultsReport
                title="Resultados semanales"
                report={resultsReport}
                weeks={weeks}
                targets={salesTargets}
                targetNotice={targetNotice}
                onConfigure={openTargets}
                configurationReady={configurationReady && !targetBusy}
                configurationError={targetError}
                selectedWeekIndex={selectedWeekIndex}
                onSelectWeek={setSelectedWeekIndex}
              />
            </TabPanel>
            <TabPanel header="Balance semanal">
              <WeeklyFinancialReportTable
                title="Balance semanal"
                report={actionableBalanceReport(data.balanceSheet)}
                weeks={weeks}
                reportType="balance"
                selectedWeekIndex={selectedWeekIndex}
                onSelectWeek={setSelectedWeekIndex}
              />
            </TabPanel>
          </TabView>
        </>
      ) : null}
    </div>
  );
};

export default OkrFinancialIndicators;
